import type { MermaidComponentProps } from '@customerio/hydra-copilot/react';
import { Box, Divider, Icon } from '@customerio/pluma-components/react';
import { useSnackbars } from '@customerio/pluma-components/react';
import { copyToClipboard } from '@/hooks/user-actions/use-copy-to-clipboard';
import { download } from '@/utils/download';
import { useMutation } from '@tanstack/react-query';
import { MessageAction, MessageActionsContainer } from '../message-container';
import * as styles from './mermaid-diagram.css';

/**
 * Prepares a mermaid SVG for standalone file use.
 *
 * Mermaid renders SVGs with `width="100%"` and a max-width style, which works
 * in-browser but breaks in standalone SVG viewers (Preview, Illustrator, etc.)
 * that have no parent element to derive a percentage width from.
 *
 * This extracts explicit dimensions from the viewBox, removes HTML-only styles,
 * and serializes as proper XML with a declaration so the file is self-contained.
 */
export function prepareSvgForExport(svgHtml: string): string {
  const container = document.createElement('div');
  container.innerHTML = svgHtml;
  const svg = container.querySelector('svg');
  if (svg == null) return svgHtml;

  // Set explicit dimensions from viewBox for standalone rendering
  const viewBox = svg.getAttribute('viewBox');
  if (viewBox != null) {
    const parts = viewBox.split(/\s+/);
    if (parts.length === 4) {
      svg.setAttribute('width', parts[2]);
      svg.setAttribute('height', parts[3]);
    }
  }

  // max-width only makes sense in an HTML context
  svg.style.removeProperty('max-width');

  // XMLSerializer produces valid XML with proper namespace declarations
  const serializer = new XMLSerializer();
  return `<?xml version="1.0" encoding="UTF-8"?>\n${serializer.serializeToString(svg)}`;
}

export function CopilotMermaidDiagram({ result }: MermaidComponentProps) {
  const snackbars = useSnackbars();

  const copyMutation = useMutation({
    mutationFn: async () => {
      if (result.status !== 'ok') return;
      const success = await copyToClipboard(result.code);
      if (!success) throw new Error('Failed to copy');
    },
    onSuccess: () => {
      snackbars.create({ message: 'Diagram code copied to clipboard.' });
    },
    onError: () => {
      snackbars.create({ message: 'Failed to copy diagram code', variant: 'critical' });
    },
  });

  const downloadMutation = useMutation({
    mutationFn: async () => {
      if (result.status !== 'ok') return;
      const svgString = prepareSvgForExport(result.svg);
      const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
      await download(blob, 'diagram.svg');
    },
    onSuccess: () => {
      snackbars.create({ message: 'Diagram exported.' });
    },
    onError: () => {
      snackbars.create({ message: 'Failed to export diagram', variant: 'critical' });
    },
  });

  if (result.status === 'error') {
    return <div className={styles.errorContainer}>This diagram couldn't be rendered.</div>;
  }

  if (result.status !== 'ok') return null;

  return (
    <Box display="flex" flexDirection="column" gap="050">
      {/* oxlint-disable-next-line react/no-danger -- mermaid v11 sanitizes SVG via DOMPurify with securityLevel: 'strict' */}
      {/* nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml */}
      <div className={styles.diagramContainer} dangerouslySetInnerHTML={{ __html: result.svg }} />
      <Divider />
      <MessageActionsContainer isVisible={true} alignBlock="center" alignInline="start">
        <MessageAction tooltip="Copy mermaid code" onClick={() => copyMutation.mutate()} aria-label="Copy mermaid code">
          <Icon name="copy-to-clipboard" size="sm" />
        </MessageAction>
        <MessageAction tooltip="Download as SVG" onClick={() => downloadMutation.mutate()} aria-label="Download as SVG">
          {downloadMutation.isPending ? <Icon name="loading" size="sm" /> : <Icon name="download" size="sm" />}
        </MessageAction>
      </MessageActionsContainer>
    </Box>
  );
}
